import { PencilSquareIcon } from "@heroicons/react/16/solid";
import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { PropsWithChildren, useState } from "react";

import { Badge, Headline, Input } from "@fronterahealth/frontera-ui-components";

import { ReportTemplateType } from "@api/graphql/types-and-hooks";
import { BadgeSettingFromStatus } from "@components/TemplatesTable/TemplatesTable";
import { convertReadableString } from "@components/forms/utils";

export interface TemplateHeaderProps extends PropsWithChildren {
  reportTemplate: ReportTemplateType;
  readonly: boolean;
  setReportTemplate: (template: ReportTemplateType) => void;
  onSaveTitle?: (newValue: string) => Promise<void>;
  isDescription?: boolean;
}

export const TemplateHeader: React.FC<TemplateHeaderProps> = ({
  reportTemplate,
  readonly,
  setReportTemplate,
  onSaveTitle,
  isDescription = false,
}) => {
  const initialContent = isDescription ? reportTemplate.description ?? "" : reportTemplate.name;

  const [isEditing, setIsEditing] = useState(false);
  const [newContent, setNewContent] = useState<string>(initialContent);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!onSaveTitle) {
      setReportTemplate({
        ...reportTemplate,
        [isDescription ? "description" : "name"]: newContent,
      });
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await onSaveTitle(newContent);
      setReportTemplate({
        ...reportTemplate,
        [isDescription ? "description" : "name"]: newContent,
      });
      setIsEditing(false);
    } catch (error) {
      console.error(`Error saving ${isDescription ? "description" : "title"}:`, error);
      setNewContent(initialContent);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setNewContent(initialContent);
    setIsEditing(false);
  };

  if (isDescription) {
    return (
      <div className="mt-2 flex flex-row items-center text-m text-gray-500">
        {isEditing ? (
          <div className="flex items-center gap-x-2 w-full">
            <Input
              className="w-full text-m text-gray-700"
              type="text"
              defaultValue={newContent}
              onChange={(e) => setNewContent(e.currentTarget.value)}
              autoFocus
              name=""
              label=""
            />
            <XMarkIcon
              onClick={(e) => {
                e.stopPropagation();
                handleCancelEdit();
              }}
              className="h-5 w-5 cursor-pointer rounded-md border border-limestone-200 bg-transparent p-1 text-text-secondary transition-all hover:bg-limestone-50"
            />
            <div
              className={`flex h-5 w-5 items-center justify-center rounded-md border border-limestone-200 ${
                isSaving ? "opacity-50" : ""
              }`}
            >
              <CheckIcon
                onClick={handleSave}
                className={`h-5 w-5 p-1 ${isSaving ? "cursor-not-allowed" : "cursor-pointer"} text-text-secondary`}
              />
            </div>
          </div>
        ) : (
          <div
            className={`${readonly ? "cursor-default" : "cursor-pointer"} flex items-center`}
            onClick={(e) => {
              e.stopPropagation();
              if (readonly) return;
              setIsEditing(true);
            }}
          >
            <div className="text-m text-gray-500">
              {newContent || <span className="text-gray-400 italic">No description</span>}
            </div>
            {!readonly && (
              <PencilSquareIcon
                onClick={(e) => {
                  e.stopPropagation();
                  setIsEditing(true);
                }}
                className="h-5 w-5 ml-2 cursor-pointer rounded-2xl bg-limestone-50 hover:bg-limestone-100 p-1 text-text-secondary"
              />
            )}
          </div>
        )}
      </div>
    );
  }

  // Default (title) rendering
  return (
    <div className={`flex w-full ${isEditing && "pb-2"}`}>
      <div data-testid="section-title-row" className="flex items-center relative group grow">
        {isEditing ? (
          <div className="w-full">
            <Input
              className="peer [&_input]:text-4xl [&_div]:w-full w-full max-w-none! font-semibold block border-0 py-1.5 text-text-primary placeholder-limestone-200 ring-inset ring-limestone-200 placeholder:text-limestone-400 sm:text-sm sm:leading-6 focus:ring-0 focus:outline-hidden cursor-text"
              type="text"
              defaultValue={newContent}
              onChange={(e) => setNewContent(e.currentTarget.value)}
              autoFocus
              name=""
              label=""
            />
            <div className="absolute right-0 bottom-1 flex items-center gap-x-2 z-10">
              <XMarkIcon
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancelEdit();
                }}
                className="h-6 w-6 cursor-pointer rounded-md border border-limestone-200 bg-transparent p-1 text-text-secondary transition-all hover:bg-limestone-50"
              />
              <div
                className={`flex h-6 w-6 items-center justify-center rounded-md border border-limestone-200 ${
                  isSaving ? "opacity-50" : ""
                }`}
              >
                <CheckIcon
                  data-testid="save-section-title-button"
                  onClick={handleSave}
                  className={`h-6 w-6 p-1 ${isSaving ? "cursor-not-allowed" : "cursor-pointer"} text-text-secondary`}
                />
              </div>
            </div>
          </div>
        ) : (
          <>
            <Headline
              size="medium"
              className={`${readonly ? "cursor-default" : "cursor-pointer"}`}
              onClick={(e) => {
                e.stopPropagation();
                if (readonly) return;
                setIsEditing(true);
              }}
            >
              {newContent}
            </Headline>
            <Badge
              appearance={BadgeSettingFromStatus[reportTemplate.status]}
              text={convertReadableString(reportTemplate.status)}
              className="ml-4"
            />
            {!readonly && (
              <PencilSquareIcon
                onClick={(e) => {
                  e.stopPropagation();
                  setIsEditing(true);
                }}
                className="h-8 w-8 ml-4 cursor-pointer rounded-2xl bg-limestone-50 hover:bg-limestone-100 p-1 text-text-secondary"
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};
